import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import { Check } from "lucide-react";
import { Card, CardTitle, Input } from "campaign-builder/ui";
import {
  FieldLabel,
  FieldError,
  RequiredLabel,
  useTargetingValidation,
} from "campaign-builder/channels/flyering/targeting";
import type { BuilderStepProps } from "campaign-builder/config";
import { useTracking } from "./trackingContext";
import { isValidUrl } from "./useStaticTracking";

const CheckboxCard = ({
  checked,
  title,
  description,
  cypressId,
  onToggle,
}: {
  checked: boolean;
  title: string;
  description: string;
  cypressId: string;
  onToggle: () => void;
}) => (
  <button
    type="button"
    data-cypress-id={cypressId}
    onClick={onToggle}
    className={cn(
      "flex flex-1 items-start gap-3 rounded-lg border border-solid p-4 text-left",
      checked ? "border-[#222336] dark:border-slate-300" : "border-border"
    )}
  >
    <span
      className={cn(
        "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border border-solid",
        checked ? "border-primary bg-primary" : "border-slate-300 dark:border-accent"
      )}
    >
      {checked && <Check className="h-3 w-3 text-primary-foreground" strokeWidth={3} />}
    </span>
    <span className="flex flex-col gap-0.5">
      <span className="text-foreground text-sm font-medium leading-5">{title}</span>
      <span className="text-muted-foreground text-xs leading-4">{description}</span>
    </span>
  </button>
);

/**
 * Static-only Tracking step shared by H2H/D2D. Two independent, fully-optional
 * feature toggles; each enabled toggle reveals one required static field. No
 * unique/same modes, placeholders, or variable files (epic T2). The form state is
 * owned by the mount (TrackingContext) so it survives leaving and re-entering.
 */
export const StaticTrackingStep: React.FC<BuilderStepProps> = () => {
  const { state, update, persistCoupon, persistQr, isValid } = useTracking();
  const { version: validationVersion } = useTargetingValidation();

  const [showErrors, setShowErrors] = useState(false);
  // Seed with the current version so a validation bump from another step (the step
  // unmounts on leave, resetting this ref) doesn't surface errors the moment the
  // user re-enters Tracking with a toggle on but no value filled.
  const lastValidation = useRef(validationVersion);
  useEffect(() => {
    if (validationVersion === lastValidation.current) return;
    lastValidation.current = validationVersion;
    if (!isValid) {
      setShowErrors(true);
      requestAnimationFrame(() => {
        document
          .querySelector('[data-targeting-section="tracking"]')
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [validationVersion, isValid]);

  const couponError =
    showErrors && state.promoEnabled && !state.staticCouponCode.trim()
      ? "Coupon code is required when redemption coupon is enabled"
      : null;
  const qrError =
    showErrors && state.qrEnabled
      ? !state.qrCodeLink.trim()
        ? "QR code URL is required when QR code is enabled"
        : !isValidUrl(state.qrCodeLink)
        ? "Please enter a valid URL."
        : null
      : null;

  return (
    <Card className="flex flex-col gap-5 p-[25px]" data-targeting-section="tracking">
      <div className="flex flex-col gap-1">
        <CardTitle>Tracking</CardTitle>
        <p className="text-muted-foreground text-sm leading-5">
          Configure your tracking metrics to effectively assess your campaign&apos;s performance post-launch.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <FieldLabel>How will you measure this campaign?</FieldLabel>
        <p className="text-muted-foreground text-xs leading-4">
          You can use promo codes, QR codes, or a combination of both.
        </p>
        <div className="flex gap-2">
          <CheckboxCard
            checked={state.promoEnabled}
            title="Promo codes"
            description="Enable this feature if your flyer will feature a trackable code/coupon."
            cypressId="tracking-promo-toggle"
            onToggle={() => update({ promoEnabled: !state.promoEnabled })}
          />
          <CheckboxCard
            checked={state.qrEnabled}
            title="QR Codes"
            description="Enable this feature if your flyer will feature a QR Code link."
            cypressId="tracking-qr-toggle"
            onToggle={() => update({ qrEnabled: !state.qrEnabled })}
          />
        </div>
      </div>

      {state.promoEnabled && (
        <div className="flex flex-col gap-2">
          <RequiredLabel>Enter the coupon code included in your flyer.</RequiredLabel>
          <Input
            data-cypress-id="tracking-promo-input"
            value={state.staticCouponCode}
            placeholder="E.g.: PROMO50"
            className={cn(couponError && "border-destructive")}
            onChange={(event) => update({ staticCouponCode: event.target.value })}
            onBlur={persistCoupon}
          />
          <FieldError message={couponError} />
        </div>
      )}

      {state.qrEnabled && (
        <div className="flex flex-col gap-2">
          <RequiredLabel>What is the QR code URL?</RequiredLabel>
          <Input
            data-cypress-id="tracking-qr-input"
            value={state.qrCodeLink}
            placeholder="https://"
            className={cn(qrError && "border-destructive")}
            onChange={(event) => update({ qrCodeLink: event.target.value })}
            onBlur={persistQr}
          />
          <FieldError message={qrError} />
        </div>
      )}
    </Card>
  );
};
